import { useState, useEffect, useCallback } from 'react';
import { cacheManager } from '../repository/api';

// Custom hook for managing API data fetching with loading states
export const useApiData = (fetchFunction, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await fetchFunction();
      setData(result);
    } catch (err) {
      setError(err.message || 'An error occurred while fetching data');
      console.error('API fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, dependencies);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch
  };
};

// Hook for managing multiple API calls with individual loading states
export const useMultipleApiData = (apiCalls) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});

  const fetchAllData = useCallback(async () => {
    const promises = Object.entries(apiCalls).map(async ([key, fetchFunction]) => {
      try {
        setLoading(prev => ({ ...prev, [key]: true }));
        setErrors(prev => ({ ...prev, [key]: null }));
        
        const result = await fetchFunction();
        setData(prev => ({ ...prev, [key]: result }));
      } catch (err) {
        setErrors(prev => ({ 
          ...prev, 
          [key]: err.message || 'An error occurred while fetching data' 
        }));
        console.error(`API fetch error for ${key}:`, err);
      } finally {
        setLoading(prev => ({ ...prev, [key]: false }));
      }
    });

    await Promise.all(promises);
  }, [apiCalls]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const refetch = useCallback((key) => {
    if (key && apiCalls[key]) {
      // Refetch specific key
      const fetchFunction = apiCalls[key];
      fetchFunction().then(result => {
        setData(prev => ({ ...prev, [key]: result }));
        setLoading(prev => ({ ...prev, [key]: false }));
        setErrors(prev => ({ ...prev, [key]: null }));
      }).catch(err => {
        setErrors(prev => ({ 
          ...prev, 
          [key]: err.message || 'An error occurred while fetching data' 
        }));
        setLoading(prev => ({ ...prev, [key]: false }));
      });
    } else {
      // Refetch all
      fetchAllData();
    }
  }, [apiCalls, fetchAllData]);

  const isLoading = Object.values(loading).some(loading => loading);
  const hasErrors = Object.values(errors).some(error => error);

  return {
    data,
    loading,
    errors,
    isLoading,
    hasErrors,
    refetch
  };
};

// Hook for managing cache operations
export const useCache = () => {
  const clearCache = useCallback(() => {
    cacheManager.clear();
  }, []);

  const clearExpiredCache = useCallback(() => {
    cacheManager.clearExpired();
  }, []);

  return {
    clearCache,
    clearExpiredCache
  };
};
